import {
  useCallback,
  useMemo,
  type CSSProperties,
  type MutableRefObject,
} from 'react'
import { gridCellClasses } from '@/components/grid/DataGrid'
import type { GridCellCtx, GridColumn } from '@/components/grid/types'
import { unitValueGetter, type UnitConverters } from '@/components/grid/unitColumn'
import {
  COLUMN_CONFIG,
  DESCRIPTION_FIELD,
  GUIDED_HIDDEN_FIELDS,
  HIDDEN_FIELDS,
} from './constants'
import { editableCellStyle } from './cell-styles'
import { isDraftId } from './tree-data'
import type { HydraulicTreeRow } from './types'

interface UseHydraulicColumnsArgs {
  bulkEditMode: boolean
  isViewOnly: boolean
  isDark: boolean
  t: (key: string, options?: any) => string
  convertLabelUnit: (unit: string) => string
  /** Capacity is stored in l/m and edited in whatever the user reads in. */
  units: UnitConverters
  editedCellsRef: MutableRefObject<Set<string>>
  /** Bumped on every edit, so the cells re-read `editedCellsRef`. */
  editVersion: number
  /** SCRUM-1032 — the guided setup shows a trimmed, always-editing list. */
  guidedSetup?: boolean
  invalidCells: Set<string>
  renderers: {
    ActionCell: (ctx: GridCellCtx<HydraulicTreeRow>) => JSX.Element | null
    CapacityCell: (ctx: GridCellCtx<HydraulicTreeRow>) => JSX.Element | null
    DescriptionCell: (ctx: GridCellCtx<HydraulicTreeRow>) => JSX.Element | null
    FertigationCell: (ctx: GridCellCtx<HydraulicTreeRow>) => JSX.Element | null
    FlowRuleCell: (ctx: GridCellCtx<HydraulicTreeRow>) => JSX.Element | null
    StateCell: (ctx: GridCellCtx<HydraulicTreeRow>) => JSX.Element | null
    StateHeader: () => JSX.Element
    flowValueOf: (row: HydraulicTreeRow, field: string) => any
  }
}

const useHydraulicColumns = ({
  bulkEditMode,
  isViewOnly,
  isDark,
  t,
  convertLabelUnit,
  units,
  editedCellsRef,
  editVersion,
  guidedSetup = false,
  invalidCells,
  renderers,
}: UseHydraulicColumnsArgs) => {
  const {
    ActionCell,
    CapacityCell,
    DescriptionCell,
    FertigationCell,
    FlowRuleCell,
    StateCell,
    StateHeader,
    flowValueOf,
  } = renderers

  const availableColumns = useMemo(
    () =>
      COLUMN_CONFIG.filter(
        (col) =>
          !HIDDEN_FIELDS.has(col.field) &&
          !(guidedSetup && GUIDED_HIDDEN_FIELDS.has(col.field)),
      ),
    [guidedSetup],
  )

  const cellStyleFor = useCallback(
    (field: string, isEditable: boolean) =>
      ({ row }: GridCellCtx<HydraulicTreeRow>): CSSProperties => {
        // The chip row and the staged drafts carry their own look, so they
        // take no per-cell fill: see `editableCellStyle`. Nor does a cell a
        // save refused: the amber "edited" fill is inline and would win over
        // the red invalid class, so an existing pipe at -10 read orange where
        // a new one read red (round 7 follow-up).
        const plain =
          Boolean(row?.isCreateRow) ||
          isDraftId(row?.id) ||
          invalidCells.has(`${row?.id}::${field}`)
        return editableCellStyle({
          bulkEditMode,
          isEditable,
          plain,
          isEdited: !plain && editedCellsRef.current.has(`${row?.id}::${field}`),
          isDark,
        })
      },
    // `editVersion` is what makes a cell re-read `editedCellsRef` after an
    // edit; the ref itself cannot trigger a render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [bulkEditMode, isDark, editVersion, invalidCells],
  )

  /** Marks a cell a save refused to write. */
  const cellClassFor = useCallback(
    (field: string) =>
      ({ row }: GridCellCtx<HydraulicTreeRow>) =>
        invalidCells.has(`${row?.id}::${field}`) ? gridCellClasses.invalid : '',
    [invalidCells],
  )

  const descriptionColumn = useMemo<GridColumn<HydraulicTreeRow>>(
    () => ({
      field: DESCRIPTION_FIELD,
      headerName: t('headers.pipeName'),
      minWidth: 220,
      // Past this the column is just holding empty space: a deeply indented
      // name still fits, and beyond it the gap between a pipe and its
      // capacity grows faster than the name ever will.
      maxWidth: 560,
      flex: 2,
      sortable: false,
      editor: 'text',
      editable: ({ row }) => !row.isCreateRow && bulkEditMode && !isViewOnly,
      cellRenderer: DescriptionCell,
      cellClass: cellClassFor(DESCRIPTION_FIELD),
      cellStyle: (ctx) => {
        const base = cellStyleFor(DESCRIPTION_FIELD, !isViewOnly)(ctx)
        // Mainlines (top-level pipes) are bold so each branch's root stands
        // out from its sub-pipes.
        const isRoot = !ctx.row.isCreateRow && !ctx.row.parentId
        return { ...base, fontWeight: isRoot ? 700 : 400 }
      },
    }),
    [t, bulkEditMode, isViewOnly, DescriptionCell, cellStyleFor, cellClassFor],
  )

  /**
   * Greys a column out and stops it taking clicks while a bulk edit is in
   * flight. `pointer-events: none` covers the buttons inside the cell and
   * their tooltips in one go, so nothing has to be disabled individually.
   */
  const dimmedInBulkEdit = useCallback(
    (
        inner?:
          | CSSProperties
          | ((ctx: GridCellCtx<HydraulicTreeRow>) => CSSProperties | undefined),
      ) =>
      (ctx: GridCellCtx<HydraulicTreeRow>): CSSProperties => {
        const base = (typeof inner === 'function' ? inner(ctx) : inner) ?? {}
        if (!bulkEditMode) return base
        return { ...base, opacity: 0.4, pointerEvents: 'none' }
      },
    [bulkEditMode],
  )

  /**
   * SCRUM-1032: the flow rule stays live in the guided setup. Everywhere else
   * bulk edit is a batch the user opens and closes, so a control that navigates
   * away from it is dimmed until they are done; in the wizard bulk edit is
   * simply the mode the step is in — dimming it there would mean the flow rule
   * could never be set up at all. What made dimming safe is now handled by
   * autosave: the batch is written on a pause rather than held indefinitely.
   */
  const dimmedUnlessGuided = useCallback(
    (
      inner?:
        | CSSProperties
        | ((ctx: GridCellCtx<HydraulicTreeRow>) => CSSProperties | undefined),
    ) => (guidedSetup ? inner : dimmedInBulkEdit(inner)),
    [guidedSetup, dimmedInBulkEdit],
  )

  const columns = useMemo<GridColumn<HydraulicTreeRow>[]>(
    () =>
      availableColumns.map((col) => {
        const valueType = (col as any).valueType as string
        const isEditable = Boolean((col as any).editable) && bulkEditMode

        const base: GridColumn<HydraulicTreeRow> = {
          field: col.field,
          headerName: col.headerName ? t(col.headerName) : '',
          minWidth: col.minWidth,
          maxWidth: (col as any).maxWidth,
          // A column that declares `flex: 0` keeps its width; the rest share
          // what is left, which in practice means Description absorbs it.
          flex: (col as any).flex ?? 1,
          sortable: Boolean(col.sortable),
          filter: (col as any).filter,
          editable: ({ row }) => !row.isCreateRow && isEditable,
          cellStyle: cellStyleFor(col.field, isEditable),
          cellClass: cellClassFor(col.field),
        }

        switch (valueType) {
          case 'action':
            return {
              ...base,
              // Leftmost and pinned, so the row's actions are reachable
              // however far the tree is scrolled sideways. The header is
              // deliberately blank — a column of icons labels itself.
              headerName: '',
              pinned: 'left',
              // The column only ever shows in bulk edit (see the visibility
              // effect), where it holds one delete icon — so it is sized to
              // the icon rather than to the config's 240px.
              minWidth: 52,
              maxWidth: 52,
              flex: 0,
              editable: false,
              cellRenderer: ActionCell,
            }
          case 'rowId':
            return {
              ...base,
              // A draft has no id to show — it has not been written yet.
              cellRenderer: ({ row }) =>
                row.isCreateRow || isDraftId(row.id) ? null : <>{row.id}</>,
            }
          // Bulk edit is about the pipe's own fields. State is read-only
          // status, and the flow-rule and fertigation controls open
          // workspaces that would take the user out of a half-finished
          // batch — so they are dimmed and inert until the batch is
          // saved or cancelled.
          case 'rule':
            return {
              ...base,
              cellStyle: dimmedUnlessGuided(base.cellStyle),
              cellRenderer: FlowRuleCell,
            }
          case 'fertigation':
            return {
              ...base,
              cellStyle: dimmedInBulkEdit(base.cellStyle),
              cellRenderer: FertigationCell,
            }
          case 'state':
            return {
              ...base,
              cellStyle: dimmedInBulkEdit(base.cellStyle),
              cellRenderer: StateCell,
              headerRenderer: StateHeader,
            }
          case 'flowValue': {
            const column: GridColumn<HydraulicTreeRow> = {
              ...base,
              headerName: `${base.headerName} (${convertLabelUnit('l/m')})`,
              editor: 'number',
              // Right-aligns cell and header to match the editor. All four flow
              // columns get it, not just the editable one, so the group reads
              // as a block of figures rather than a mix.
              numeric: true,
              cellRenderer:
                col.field === 'capacity'
                  ? CapacityCell
                  : ({ row }: GridCellCtx<HydraulicTreeRow>) =>
                      row.isCreateRow ? null : <>{flowValueOf(row, col.field)}</>,
            }
            // Capacity is the only editable one, and it needs the editor to
            // open on the same number the cell shows — it used to open on the
            // stored l/m under a gpm heading, so typing 20 wrote a pipe about
            // four times smaller than intended.
            //
            // Getter only: this page's onCellValueChanged returns false and
            // stages edits itself, so a valueSetter here would never run. The
            // conversion back to metric happens where the edit is staged.
            if (col.field === 'capacity') {
              column.valueGetter = unitValueGetter<HydraulicTreeRow>(
                col.field,
                'l/m',
                units,
              )
              // SCRUM-1032: in the guided setup Capacity is the one figure on
              // the row, and it sits centred under its heading.
              if (guidedSetup) {
                const invalidClass = cellClassFor(col.field)
                column.cellClass = (ctx) => [
                  invalidClass(ctx),
                  gridCellClasses.centered,
                ]
              }
            }
            return column
          }
          default:
            return {
              ...base,
              cellRenderer: ({ row, value }: GridCellCtx<HydraulicTreeRow>) =>
                row.isCreateRow ? null : <>{(value as React.ReactNode) ?? null}</>,
            }
        }
      }),
    [
      availableColumns,
      bulkEditMode,
      t,
      convertLabelUnit,
      units,
      cellStyleFor,
      cellClassFor,
      dimmedInBulkEdit,
      dimmedUnlessGuided,
      guidedSetup,
      ActionCell,
      FlowRuleCell,
      FertigationCell,
      StateCell,
      StateHeader,
      CapacityCell,
      flowValueOf,
    ],
  )

  return { availableColumns, columns, descriptionColumn }
}

export default useHydraulicColumns
